/**
 * Модальное окно.
 *
 * Закрывается по Escape и по клику мимо: это первое, что пробует человек,
 * открывший его случайно. Окно, которое нельзя просто закрыть — например,
 * выбор «отменить удаление или выйти», — передаёт dismissible={false}.
 */
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export function Dialog({
  title,
  children,
  onClose,
  dismissible = true,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  dismissible?: boolean;
}) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dismissible) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, dismissible]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)] p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (dismissible && !box.current?.contains(event.target as Node)) onClose();
      }}
    >
      {/* Высокое окно не должно уезжать за края экрана: длинная справка или
          форма с несколькими полями иначе теряют заголовок сверху и кнопку
          снизу. Шапка остаётся на месте, прокручивается только содержимое. */}
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        className="panel flex max-h-[90svh] w-full max-w-md flex-col overflow-hidden"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-ink-800 px-5 py-3">
          <h2 className="font-sans text-xl text-ink-50">{title}</h2>
          {dismissible && (
            <button
              type="button"
              className="btn-ghost px-2 py-1 text-sm"
              onClick={onClose}
              aria-label={t("common.close")}
            >
              ✕
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}
